import type { FormEvent } from 'react'
import { useState, useEffect } from 'react'
import { LinkIcon, SparklesIcon, BoltIcon, PencilSquareIcon } from '@heroicons/react/24/outline'

const EXTRACTION_MESSAGES = [
  { text: 'Extracting job details...', minDuration: 0 },
  { text: 'Analyzing posting content...', minDuration: 5000 },
  { text: 'Looking up company information...', minDuration: 12000 },
  { text: 'This is taking a bit longer than usual...', minDuration: 20000 },
  { text: 'Almost there...', minDuration: 30000 },
]

type Props = {
  formUrl: string
  isSubmitting: boolean
  formError: string | null
  onSubmit: (e: FormEvent, quickAdd?: boolean) => void
  onChangeUrl: (value: string) => void
  onManualAdd: () => void
}

export const AddApplicationForm = ({
  formUrl,
  isSubmitting,
  formError,
  onSubmit,
  onChangeUrl,
  onManualAdd,
}: Props) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0)

  useEffect(() => {
    if (!isSubmitting) {
      setCurrentMessageIndex(0)
      return
    }

    let messageIdx = 0
    setCurrentMessageIndex(0)

    const checkNextMessage = () => {
      const nextIdx = messageIdx + 1
      if (nextIdx < EXTRACTION_MESSAGES.length) {
        const nextMsg = EXTRACTION_MESSAGES[nextIdx]
        const currentMsg = EXTRACTION_MESSAGES[messageIdx]
        const delay = nextMsg.minDuration - currentMsg.minDuration

        setTimeout(() => {
          if (isSubmitting) {
            messageIdx = nextIdx
            setCurrentMessageIndex(nextIdx)
            checkNextMessage()
          }
        }, delay)
      }
    }

    checkNextMessage()
  }, [isSubmitting])

  return (
    <div
      data-tour="add-job"
      className="relative overflow-hidden rounded-2xl border border-app-line bg-none bg-app-highlight shadow-app-card"
    >
      <div className="relative p-3">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-app-primary/20 text-app-accent">
          <SparklesIcon className="w-3.5 h-3.5" />
        </div>
        <h2 className="text-sm font-semibold text-app-ink font-display">Add new application</h2>
      </div>

      <form onSubmit={(e) => onSubmit(e, false)}>
        <div className="flex flex-col sm:flex-row gap-2">
          {/* URL input */}
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-app-muted">
              <LinkIcon className="w-4 h-4" />
            </div>
            <input
              type="url"
              placeholder="Paste job posting URL..."
              value={formUrl}
              onChange={(e) => onChangeUrl(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-app-line bg-app-surface/80 text-sm text-app-ink placeholder:text-app-muted focus:border-app-accent focus:ring-1 focus:ring-app-accent/50 focus:outline-none transition-colors"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            {/* Main submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-app-primary text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-60 disabled:hover:bg-app-primary-hover transition-all hover:shadow-app-hover shadow-app-card"
            >
              {isSubmitting ? (
                <>
                  <span
                    className="h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent"
                    aria-hidden="true"
                  />
                  <span className="hidden sm:inline">{EXTRACTION_MESSAGES[currentMessageIndex].text}</span>
                  <span className="sm:hidden">Extracting...</span>
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Add & Review</span>
                  <span className="sm:hidden">Add</span>
                </>
              )}
            </button>

            {/* Quick add button */}
            <button
              type="button"
              disabled={isSubmitting}
              onClick={(e) => onSubmit(e as unknown as FormEvent, true)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-app-line bg-app-subtle/80 text-xs font-medium text-app-muted hover:bg-app-line hover:text-app-ink disabled:opacity-60 transition-all"
              title="Add without reviewing - uses extracted data as-is"
            >
              <BoltIcon className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Quick Add</span>
            </button>

            {/* Manual add button */}
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onManualAdd}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-app-line bg-app-subtle/80 text-xs font-medium text-app-muted hover:bg-app-line hover:text-app-ink disabled:opacity-60 transition-all"
              title="Add application manually without a URL"
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Manual</span>
            </button>
          </div>
        </div>

        {/* Helper text */}
        <p className="mt-2 text-[10px] text-app-muted text-center sm:text-left">
          <span className="text-app-accent">Add & Review</span> lets you edit extracted data •
          <span className="text-app-muted"> Quick Add</span> saves immediately •
          <span className="text-app-muted"> Manual</span> for entries without URL
        </p>

        {formError && (
          <p className="mt-2 text-sm text-rose-700 dark:text-rose-400 flex items-center gap-2">
            <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-rose-400" />
            {formError}
          </p>
        )}
      </form>
    </div>
    </div>
  )
}
